/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React from 'react';
import styled from 'styled-components';
import {
  SYS_BREAKPOINT_2_XL,
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_SM,
  SYS_BREAKPOINT_XL,
  SYS_BREAKPOINT_XS,
  SYS_SPACE_FIXED_300,
} from '@oneaudi/unified-web-common';
import type {
  Layout,
  LinkProps,
  HeadlineProps,
  ModuleContent,
  ImageProps,
} from '../../EditorContentTypes';
import CTAs from './CTAs';
import Headliner from './Headline';
import Image from './Image';
import type { LegalDataProps } from './LegalData';
import LegalData from './LegalData';
import Text from './Text';

export const BackgroundWrapper = styled.div`
  background-color: ${process.env.IS_LEGACY ? ({ theme }) => theme.colors.colorCanvasDefault : ''};
  color: ${process.env.IS_LEGACY ? ({ theme }) => theme.colors.colorCopyDefault : ''};
`;

const StyledBlockContainer = styled.div`
  box-sizing: border-box;

  display: flex;
  justify-content: left;
  flex-direction: column-reverse;

  &.img-left,
  &.img-right {
    @media (min-width: ${SYS_BREAKPOINT_XS}) {
      gap: var(--spacing-relative-xl);
    }
  }

  & > * {
    flex: 0 1 50%;
  }

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    padding: var(--spacing-relative-xl) var(--page-margin);

    &.img-left,
    &.img-right {
      gap: var(--spacing-relative-2xl);
    }
    &.img-left {
      flex-direction: row-reverse;
    }

    &.img-right {
      flex-direction: row;
    }
  }

  @media (min-width: ${SYS_BREAKPOINT_LG}) {
    margin-inline: 0;
  }

  @media (min-width: ${SYS_BREAKPOINT_XL}) {
    &.article-width {
      max-width: 749px;
      margin-inline: auto;
      padding-inline: 0;
    }
  }

  @media (min-width: ${SYS_BREAKPOINT_2_XL}) {
    max-width: 1440px;
    margin-inline: auto;
    &.article-width {
      max-width: 849px;
      padding-inline: 0;
    }
  }

  @media (max-width: ${SYS_BREAKPOINT_SM}) {
    :last-child {
      margin-block-start: ${SYS_SPACE_FIXED_300};
    }
  }
`;

const TextContainer = styled.div`
  ${StyledBlockContainer}.img-top &.with-headline {
    margin-block-start: ${SYS_SPACE_FIXED_300};
  }

  ${StyledBlockContainer}.img-top &.without-headline {
    margin-block-start: var(--spacing-relative-3xl);
  }

  &.text-only {
    margin-block-start: 0;
  }

  @media (min-width: ${SYS_BREAKPOINT_XS}) {
    padding-inline: var(--page-margin);
  }

  @media (max-width: ${SYS_BREAKPOINT_MD}) {
    padding-bottom: var(--spacing-relative-3xl);
  }

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    padding-inline: 0;
    margin-inline: 0;
  }
  &.legal-text {
    padding-bottom: 0;
  }
`;

export interface BlockProps {
  readonly moduleContent: ModuleContent;
  readonly layout?: Layout;
  readonly headline?: HeadlineProps;
  readonly text?: string;
  readonly articleWidth?: boolean;
  readonly image: ImageProps;
  readonly links?: LinkProps[];
  readonly legalData?: LegalDataProps;
  readonly moduleIndex: number;
  readonly additionalModule: boolean;
}

// eslint-disable-next-line react/display-name
const Block: React.FC<BlockProps> = React.memo(
  ({
    moduleContent,
    layout,
    headline,
    text,
    articleWidth,
    image,
    links,
    legalData,
    moduleIndex,
    additionalModule,
  }: BlockProps) => {
    const isTextOnly = moduleContent === `text-only`;
    const showImage = !isTextOnly;
    const isArticleWidth = articleWidth && isTextOnly;
    const imageAvailable =
      image.imageDesktop.assetValue != '' && image.imageMobile.assetValue != '';
    return (
      <StyledBlockContainer className={`${layout ?? ''} ${isArticleWidth ? 'article-width' : ''}`}>
        {(text || headline || (links && links?.length > 0)) && (
          <TextContainer
            className={`${
              headline && headline?.text?.length > 0 ? 'with-headline' : 'without-headline'
            } ${moduleContent}`}
          >
            <>
              {headline && (
                <Headliner
                  property={additionalModule ? 'headline_text' : 'topModule_headline_text'}
                  {...headline}
                />
              )}
              {text && <Text property={additionalModule ? 'text' : 'topModule_text'} text={text} />}

              {links && (
                <CTAs
                  property0Text={additionalModule ? 'links_0_text' : 'topModule_links_0_text'}
                  property1Text={additionalModule ? 'links_1_text' : 'topModule_links_1_text'}
                  links={links}
                  moduleIndex={moduleIndex}
                />
              )}
            </>
            {isTextOnly && legalData && (
              <LegalData
                propertyWLTP={
                  additionalModule ? 'legalData_wltpKeys' : 'topModule_legalData_wltpKeys'
                }
                propertyAdditionalText={
                  additionalModule
                    ? 'legalData_additionalText'
                    : 'topModule_legalData_additionalText'
                }
                isTextOnly={isTextOnly}
                {...legalData}
              />
            )}
          </TextContainer>
        )}

        {showImage && imageAvailable && (
          <div>
            <Image
              propertyImageDesktop={
                additionalModule ? 'image_imageDesktop' : 'topModule_image_imageDesktop'
              }
              {...image}
            />
            {legalData && (
              <TextContainer className="legal-text">
                <LegalData
                  propertyWLTP={
                    additionalModule ? 'legalData_wltpKeys' : 'topModule_legalData_wltpKeys'
                  }
                  propertyAdditionalText={
                    additionalModule
                      ? 'legalData_additionalText'
                      : 'topModule_legalData_additionalText'
                  }
                  {...legalData}
                />
              </TextContainer>
            )}
          </div>
        )}
      </StyledBlockContainer>
    );
  },
);

export default Block;
